import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Typography } from 'antd';

import {
  CustomNotification,
  dialogFunction,
  FormAddButton,
  StyledModal,
  WithLoading,
} from 'common/components';

import {
  IXOneFooter,
  IXOneGrid,
  IXOneHeader,
  IXOneMessage,
  IXOneTotalFee,
  modalIXOneCaptureMinHeight,
  modalIXOneCaptureWidth,
  RIVIRBillingReview,
  useIXOneColumns,
} from '../ixone-credit-card';

import { IXOneNewProductForm } from './IXOneNewProductForm';

import { CODChargeValidating, COD_PAYMENT_TYPE_VIEW } from '../cod-payment';

import { useIXOneCapture } from '../ixone-credit-card/IXOneContext';

import productFullViewReducer from 'pages/product-full-view/controllers/reducer';
import productFullViewSaga from 'pages/product-full-view/controllers/saga';
import { useInjectReducer } from 'utils/common/injectedReducers';
import { useInjectSaga } from 'utils/common/injectSaga';

import {
  useGetProductsForCapture,
  useHierarchyAppliedProduct,
  useMutationSendProductToCapture,
} from './hooks';

import {
  productFullViewKeys,
  useGetProductFullView,
  useVisibleModal,
} from 'hooks';
import { useCheckPaymentTerm } from '../cod-payment/hooks';
import { useDispatchReloadPage } from 'hooks/useReloadPage';
import { useGetProductVersionList } from 'pages/product-history/hook';

import { getAllIXOneRows } from './utils';
import { keys } from 'pages/company-profile/components/tabs/billing/queries';
import CaptureFailedProductList from './CaptureFailedProductList';

const key = 'productFullView';

export const IXOneCapture = (props) => {
  useInjectReducer({ key, reducer: productFullViewReducer });
  useInjectSaga({ key, saga: productFullViewSaga });

  const { onCancel, productId, isDetailView, ...otherProps } = props;

  const [
    visibleReviewBilling,
    { onModal: onModalReviewBilling, offModal: offModalReviewBilling },
  ] = useVisibleModal();

  const {
    ixoneGridApi,
    setIXOneGridApi,
    selectedHierarchies,
    selectedProductIds,
  } = useIXOneCapture();

  const reloadGridProducts = useDispatchReloadPage();

  const { handleRefetchProductFullView } = useGetProductFullView({ productId });

  const { isCODTerm, isInvoicedTerm } = useCheckPaymentTerm();

  const { productList, failedProductList, isLoading, dataUpdatedAt } =
    useGetProductsForCapture({
      productIds: selectedProductIds,
    });

  const previousDataUpdatedAt = useRef(dataUpdatedAt);
  const modalRef = useRef(null);

  const { applySelectedHierarchyForMultiProduct } =
    useHierarchyAppliedProduct();

  useEffect(() => {
    if (dataUpdatedAt !== previousDataUpdatedAt.current) {
      applySelectedHierarchyForMultiProduct();
    }
    previousDataUpdatedAt.current = dataUpdatedAt;
  }, [applySelectedHierarchyForMultiProduct, dataUpdatedAt, ixoneGridApi]);

  const totalFee = useMemo(() => {
    return productList.reduce((acc, product) => {
      const hierarchyInfo = selectedHierarchies[product.productId];
      const selectedHierarchy = hierarchyInfo?.selectedHierarchy;
      const hasHierarchy =
        selectedHierarchy && Object.keys(selectedHierarchy).length > 0;

      const fee = hasHierarchy ? selectedHierarchy.fee : product.fee;
      return acc + fee;
    }, 0);
  }, [productList, selectedHierarchies]);

  const { columns } = useIXOneColumns({
    isActiveProduct: false,
    selectedHierarchies,
  });

  const captureMutation = useMutationSendProductToCapture({
    onCancel,
    meta: {
      invalidates: isDetailView
        ? [
            productFullViewKeys.itemData(productId),
            keys.allMemberBillingStatement(),
          ]
        : [keys.allMemberBillingStatement()],
    },
  });

  const { handleRefetchProductVersioning } = useGetProductVersionList({
    enabled: false,
  });

  const confirmRequest = async () => {
    //* COD no need to call /send-product-to-capture
    if (isCODTerm) {
      onCaptureSuccess();
      CustomNotification.success('Send product(s) to capture successfully');
      return;
    }

    const allRowData = getAllIXOneRows(ixoneGridApi);

    const selectedProducts = allRowData.map((row) => ({
      productItemId: row.id,
      hierarchyId: row.hierarchyId ?? null,
    }));

    await captureMutation.mutateAsync(
      { selectedProducts },
      {
        onSuccess: () => {
          onCaptureSuccess();
        },
      }
    );
  };

  const handleSendProductToCapture = () => {
    dialogFunction({
      type: 'warn',
      content: <ContentConfirmRequest onClick={onModalReviewBilling} />,
      okText: 'Agree',
      onOk: confirmRequest,
    });
  };

  const onCaptureSuccess = () => {
    if (isDetailView) {
      handleRefetchProductVersioning();
      handleRefetchProductFullView();
    } else {
      reloadGridProducts();
    }

    //* close modal
    onCancel();
  };

  const onGridReady = useCallback(
    (params) => {
      setIXOneGridApi(params);
    },
    [setIXOneGridApi]
  );

  const getChargeItems = useCallback(() => {
    return productList.map((product) => {
      const hierarchyInfo = selectedHierarchies[product.productId];
      const selectedHierarchy = hierarchyInfo?.selectedHierarchy;
      const hasHierarchy =
        selectedHierarchy && Object.keys(selectedHierarchy).length > 0;

      return {
        productId: product.productId,
        hierarchyId: hasHierarchy ? selectedHierarchy.productHierarchyId : null,
        fee: hasHierarchy ? selectedHierarchy.fee : product.fee,
        gtin: product.gtin,
        productName: product.productName,
      };
    });
  }, [productList, selectedHierarchies]);

  useEffect(() => {
    if (failedProductList?.length) {
      if (modalRef.current) {
        modalRef.current.destroy();
      }

      modalRef.current = dialogFunction({
        type: 'warn',
        content: (
          <CaptureFailedProductList failedProductList={failedProductList} />
        ),
        cancelButtonProps: { style: { display: 'none' } },
        width: 900,
        centered: true,
        destroyOnClose: true,
      });
    }
  }, [JSON.stringify(failedProductList)]);

  return (
    <CODChargeValidating
      fee={totalFee}
      typeView={COD_PAYMENT_TYPE_VIEW.sendIXOneCapture}
      confirmRequest={confirmRequest}
      // chargeItems={getAllIXOneRows(ixoneGridApi)}
      chargeItems={getChargeItems()}
      onCancel={onCancel}
    >
      {({ handleCODCharge }) => {
        return (
          <WithLoading loading={isLoading}>
            <IXOneModal onCancel={onCancel} {...otherProps}>
              <IXOneHeader style={{ minHeight: 110 }} />
              <IXOneMessage message='Please confirm your selection to have the following products made Public and accessible to Retail Members of RIVIR' />
              <IXOneGrid
                productList={productList?.map((product) => ({
                  ...product,
                  id: product.productId,
                }))}
                columns={columns}
                onGridReady={onGridReady}
                getRowNodeId={(data) => {
                  return data?.['productId'];
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <IXOneNewProduct />
                  <IXOneTotalFee fee={totalFee} />
                </div>
              </IXOneGrid>
              <IXOneFooter
                onCancel={props.onCancel}
                onSave={() => {
                  if (isCODTerm) {
                    handleCODCharge();
                  } else if (isInvoicedTerm) {
                    confirmRequest();
                  } else {
                    handleSendProductToCapture();
                  }
                }}
                loading={captureMutation.isLoading}
                disabled={!productList?.length}
              />
            </IXOneModal>
            {visibleReviewBilling && (
              <RIVIRBillingReview
                visible={visibleReviewBilling}
                onCancel={offModalReviewBilling}
              />
            )}
          </WithLoading>
        );
      }}
    </CODChargeValidating>
  );
};

export const IXOneModal = (props) => {
  const { children, ...otherProps } = props;

  const modalProps = {
    title: null,
    closable: false,
    destroyOnClose: true,
    width: modalIXOneCaptureWidth,
    bodyStyle: {
      minHeight: modalIXOneCaptureMinHeight,
      paddingTop: 4,
    },
    wrapClassName: 'ixone-section__modal',
    footer: null,
    ...otherProps,
  };

  return <StyledModal {...modalProps}>{children}</StyledModal>;
};

const IXOneNewProduct = () => {
  const [visibleAddProduct, { onModal, offModal }] = useVisibleModal();

  return (
    <>
      <div style={{ paddingTop: 12 }}>
        <FormAddButton text='New Product' onClick={onModal} />
      </div>

      {visibleAddProduct && (
        <IXOneNewProductForm visible={visibleAddProduct} onCancel={offModal} />
      )}
    </>
  );
};

export const ContentConfirmRequest = ({ onClick }) => {
  return (
    <>
      <Typography.Text strong>
        By submitting your data, you are agreeing to our{' '}
      </Typography.Text>
      <Typography.Text
        strong
        underline
        onClick={onClick}
        style={{ cursor: 'pointer' }}
      >
        Payment Terms & Conditions
      </Typography.Text>
    </>
  );
};
